import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Option } from 'slim-select/dist/data.d';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { RootState } from '../../../../core/state/state';
import Select from '../../../../components/form/select/Select';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { Color } from '../../../../core/enums/Color';
import CheckBox from '../../../../components/form/checkbox/CheckBox';
import Input from '../../../../components/form/input/Input';
import { Contact } from '../../../../core/state/subcontractors/types';
import { getExternalUserContacts, updateExternalUserContact } from '../../../../core/state/subcontractors/asyncActions';

import './ManageCompanyContactModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
}

type FormData = {
  contact: string;
  phoneNumber: string;
  isPrimary: boolean;
};

const ManageCompanyContactModal: React.FC<Props> = ({ isOpen, closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contacts = useSelector((state: RootState) => state.subcontractor.contacts);
  const userID = useSelector((state: RootState) => state.user.userInfo?.userID);
  const contactsToOption = (contact: Contact): Option => ({
    value: contact.id.toString(),
    text: `${contact.firstName} ${contact.lastName}`
  });

  const userOptions = useMemo(() => contacts?.map(contactsToOption), [contacts]);

  const validationSchema = yup
    .object()
    .shape({
      contact: yup.string().required()
    })
    .required();

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { isValid, errors }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      contact: '',
      phoneNumber: '',
      isPrimary: false
    },
    resolver: yupResolver(validationSchema)
  });

  const contact = watch('contact');
  const getSelectedContact = (contactId: number) => contacts?.find((u) => u.id === Number(contactId));

  const onSubmit = async (formData: FormData) => {
    const selectedContact = getSelectedContact(Number(formData.contact));
    if (selectedContact) {
      const data: Contact = { ...selectedContact, ...formData };
      await dispatch(updateExternalUserContact(data, Number(userID), Number(data.id)));
      await dispatch(getExternalUserContacts(Number(userID)));
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Success,
          message: t('company.modal.updateContact.form.message.success')
        })
      );
    }
    closeModal();
  };

  const setValuesAfterSelectingContact = (selectedContactId: number) => {
    const selectedContact = getSelectedContact(selectedContactId);
    if (selectedContact) {
      if (selectedContact.phoneNumber) {
        setValue('phoneNumber', selectedContact?.phoneNumber);
      } else {
        setValue('phoneNumber', '');
      }
      setValue('isPrimary', selectedContact?.isPrimary);
    } else {
      reset();
    }
  };

  const title = <h3 className="modal-title">{t('company.manageContacts')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="manage-company-contact" type="submit" buttonTypes={['blue']}>
        {t('common.save')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={326}>
      <form id="manage-company-contact" className="manage-company-contact" onSubmit={handleSubmit(onSubmit)}>
        <div className="manage-company-contact__content">
          <Controller
            name="contact"
            control={control}
            render={({ field: { onChange } }) => (
              <Select
                id="user"
                label={t('common.contactName')}
                name={contact}
                options={userOptions}
                onChange={(option: any) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  setValuesAfterSelectingContact(Number(option.value));
                  onChange(option.value);
                }}
                addPlaceholderOption
                placeholder={t('company.placeholder.enterContactName')}
              />
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="phoneNumber"
                error={errors.phoneNumber?.message}
                name="phoneNumber"
                label={t('common.companyNumber')}
                placeholder={t('company.modal.placeholder.contactNumber')}
                value={value}
              />
            )}
          />
          <p className="form-label mt-3">{t('company.modal.primaryContact')}</p>
          <Controller
            name="isPrimary"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CheckBox id="isPrimary" name="isPrimary" checked={value ?? true} onChange={onChange} label={t('company.modal.setContactAsPrimary')} />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default ManageCompanyContactModal;
